import { bootstrap } from "core-integration/src/store/bootstrap/thunks";
import {
  getPlayerData,
  getPlayerUpdateError,
  getRegisterError,
} from "core-integration/src/store/player/reducers";
import {
  playerUpdate,
  register,
} from "core-integration/src/store/player/thunks";
import { getRegions } from "core-integration/src/store/regions/reducers";
import { fetchRegions } from "core-integration/src/store/regions/thunks";
import range from "lodash/range";
import sortBy from "lodash/sortBy";
import values from "lodash/values";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { IPlayer } from "../../types";

import { ThunkDispatch } from "core-integration/src/store";
import { gtmPush } from "shared-utils/src/gtm";
import { fetchResidences, IResidence } from "../../utils/residence";
import Alert from "../Alert";
import Button from "../Button";
import Copy from "../Copy";
import {
  CheckboxField,
  FieldFeedback,
  InputField,
  RadioGroup,
  SelectField,
} from "../FieldRenderers";
import { Main, Wrapper } from "../Layout";
import Title from "../Title";
import DeleteAccountBox from "./DeleteAccountBox";
import PasswordChange from "./PasswordChange";

const DOBFieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
`;

const DOBFieldsetInner = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: ${({ theme }) => theme.space[2]};
    margin-left: -${({ theme }) => theme.space[2]};
    margin-right: -${({ theme }) => theme.space[2]};
  }
`;

const DOBLegend = styled.legend`
  display: block;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.space[1]};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.5rem;
  font-weight: bold;
  cursor: pointer;
`;

const DOBCol = styled.div`
  margin-top: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    width: calc(100% / 3);
    margin-top: 0;
    padding-left: ${({ theme }) => theme.space[2]};
    padding-right: ${({ theme }) => theme.space[2]};
  }
`;
interface IState {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  errorEmail: string;
  errorFName: string;
  errorLName: string;
  errorPass: string;
  region: number;
  residences: any | null;
  residenceData: any | null;
  residenceCity: number | null;
  emailSPL: boolean;
  errorRegion: string;
  gender: string;
  errorGender: string;
  formDisabled: boolean;
  errorDob: string;
  birthDay: number;
  birthMonth: number;
  birthYear: number;
}

type Props = WithTranslation;

const Register: React.FC<Props> = ({ t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();
  const player = useSelector(getPlayerData) as IPlayer | null;
  const registerError = useSelector(getRegisterError);
  const updateError = useSelector(getPlayerUpdateError);
  const regions = useSelector(getRegions);

  const doBootstrap = () => dispatch(bootstrap());
  const registerPlayer = (data: any) => dispatch(register(data));
  const updatePlayer = (data: any) => dispatch(playerUpdate(data));
  const fetchRegionData = () => dispatch(fetchRegions());

  const errorsTxt: { [key: string]: string } = {
    EDob: t("register.errors.dob", "Date of birth not valid"),
    EEmail: t("register.errors.email", "An email is required"),
    EFName: t("register.errors.fname", "First name is required"),
    ELName: t("register.errors.lname", "Last name is required"),
    EPass: t("register.errors.pass", "A password is required"),
    EPassLength: t(
      "register.errors.passLength",
      "Password too short, minimum 6 characters",
    ),
    EValidEmail: t(
      "register.errors.validEmail",
      "This Email address is already registered",
    ),
    EValidPass: t("register.errors.validPass", "Password not valid"),
    ERegion: t("register.errors.regionn", "Nationality is required"),
    EGender: t("register.errors.gender", "A gender choice is required"),
    EValidDob: t(
      "register.errors.validDobn",
      "A correct date of birth is required",
    ),
  };

  const months: { [key: string]: string } = {
    1: t("months.janShort", "Jan"),
    2: t("months.febShort", "Feb"),
    3: t("months.marShort", "Mar"),
    4: t("months.aprShort", "Apr"),
    5: t("months.mayShort", "May"),
    6: t("months.junShort", "Jun"),
    7: t("months.julShort", "Jul"),
    8: t("months.augShort", "Aug"),
    9: t("months.sepShort", "Sep"),
    10: t("months.octShort", "Oct"),
    11: t("months.novShort", "Nov"),
    12: t("months.decShort", "Dec"),
  };

  const [state, setState] = useState<IState>({
    birthDay:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[2])
        : 0,
    birthMonth:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[1])
        : 0,
    birthYear:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[0])
        : 0,
    email:
      player && player.email.indexOf("@ismfgsocial") === -1 ? player.email : "",
    errorEmail: "",
    errorFName: "",
    errorLName: "",
    errorPass: "",
    firstName: player ? player.first_name : "",
    lastName: player ? player.last_name : "",
    password: "",
    emailSPL: player ? player.email_spl : false,
    errorRegion: "",
    region: player ? player.region : 0,
    residenceData: null,
    residences:
      player && player.residence && player.residence >= 240
        ? 185
        : player && player.residence && player.residence < 240
          ? player.residence
          : 0,
    residenceCity:
      player && player.residence && player.residence >= 240
        ? player.residence
        : null,
    gender: player ? player.gender : "",
    errorGender: "",
    formDisabled: false,
    errorDob: "",
  });

  const getMode = () => {
    if (!player) {
      return "register";
    }
    return player.dirty ? "confirm" : "update";
  };

  const getRequiredFields: () => Array<keyof IState> = () => {
    switch (getMode()) {
      case "register":
        return [
          "birthDay",
          "birthMonth",
          "birthYear",
          "email",
          "firstName",
          "lastName",
          "password",
          "region",
          "gender",
        ];
      case "confirm":
        return [
          "birthDay",
          "birthMonth",
          "birthYear",
          "email",
          "firstName",
          "lastName",
          "region",
          "gender",
        ];
      case "update":
        return [
          "birthDay",
          "birthMonth",
          "birthYear",
          "email",
          "firstName",
          "lastName",
          "gender",
          "region",
        ];
    }
  };

  const apiDataFromState = () => {
    switch (getMode()) {
      case "register":
        return {
          date_of_birth: `${state.birthYear}-${state.birthMonth}-${state.birthDay}`,
          email: state.email,
          first_name: state.firstName,
          last_name: state.lastName,
          password: state.password,
          region: state.region,
          email_spl: state.emailSPL,
          gender: state.gender,
          residence:
            state.residenceCity && state.residenceCity > 0
              ? state.residenceCity
              : state.residences,
        };
      case "confirm":
        return {
          date_of_birth: `${state.birthYear}-${state.birthMonth}-${state.birthDay}`,
          email: state.email,
          first_name: state.firstName,
          last_name: state.lastName,
          region: state.region,
          email_spl: state.emailSPL,
          gender: state.gender,
          residence:
            state.residenceCity && state.residenceCity > 0
              ? state.residenceCity
              : state.residences,
        };
      case "update":
        return {
          date_of_birth: `${state.birthYear}-${state.birthMonth}-${state.birthDay}`,
          email: state.email,
          first_name: state.firstName,
          last_name: state.lastName,
          region: state.region,
          email_spl: state.emailSPL,
          gender: state.gender,
          residence:
            state.residenceCity && state.residenceCity > 0
              ? state.residenceCity
              : state.residences,
        };
    }
  };

  const handleBirthDayChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({
      ...state,
      birthDay: parseInt(e.currentTarget.value, 10),
    });

  const handleBirthMonthChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({
      ...state,
      birthMonth: parseInt(e.currentTarget.value, 10),
    });

  const handleBirthYearChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({
      ...state,
      birthYear: parseInt(e.currentTarget.value, 10),
    });

  const handleEmailChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({
      ...state,
      email: e.currentTarget.value,
      formDisabled: false,
    });

  const handleFirstNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({
      ...state,
      firstName: e.currentTarget.value,
    });

  const handleLastNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({
      ...state,
      lastName: e.currentTarget.value,
    });

  const handleGenderChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({
      ...state,
      gender: e.currentTarget.value,
    });

  const handlePasswordChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({
      ...state,
      password: e.currentTarget.value,
      formDisabled: false,
    });

  const handleRegionChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({
      ...state,
      region: parseInt(e.currentTarget.value, 10),
    });

  const handleemailSPLChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({
      ...state,
      emailSPL: e.currentTarget.checked ? true : false,
    });

  const handleResidenceChange = (
    e: React.SyntheticEvent<HTMLSelectElement>,
  ) => {
    setState({
      ...state,
      residences: parseInt(e.currentTarget.value, 10),
    });
    setState({
      ...state,
      residenceCity: 0,
    });
  };
  const handleResidenceCityChange = (
    e: React.SyntheticEvent<HTMLSelectElement>,
  ) =>
    setState({
      ...state,
      residenceCity: parseInt(e.currentTarget.value, 10),
    });

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setState({ ...state, formDisabled: true });
    const mode = getMode();

    if (mode === "register") {
      try {
        registerPlayer(apiDataFromState());
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        return;
      }
      doBootstrap();
      gtmPush({
        event: "sign_up",
        user_email: state.email,
        first_name: state.firstName,
        last_name: state.lastName,
        region: state.region,
        gender: state.gender,
        RSL: true,
      });
      navigate("/squad-selection");
    } else if (mode === "confirm") {
      try {
        updatePlayer(apiDataFromState());
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        return;
      }
      doBootstrap();
      navigate("/squad-selection");
    } else if (mode === "update") {
      try {
        updatePlayer(apiDataFromState());
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        return;
      }
      doBootstrap();
      // Maybe this should be back to my team, maybe our LoggedIn component
      navigate("/");
    }
    setState({ ...state, formDisabled: false });
  };

  useEffect(() => {
    const fetchData = async () => {
      fetchRegionData();
      await fetchResidences().then((data) => {
        setState({ ...state, residenceData: data });
      });
    };

    fetchData();
  }, []);

  const getSaudiRegions = () => {
    const regs = regions;
    const saudi = regs.filter((reg) => reg.code === 185);
    return sortBy(saudi, ["name"]);
  };

  const getIntRegions = () => {
    const regs = regions;
    const noSaudi = regs.filter((reg) => reg.code !== 185);
    return sortBy(noSaudi, ["name"]);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getSaudiResidenceCities = () => {
    const regs = regions;
    const saudi = regs.filter((reg) => reg.code > 185);
    return sortBy(saudi, ["name"]);
  };

  const handleFirstnameBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorFName: errorsTxt.EFName });
    } else {
      setState({ ...state, errorFName: "" });
    }
  };

  const handleLastnameBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorLName: errorsTxt.ELName });
    } else {
      setState({ ...state, errorLName: "" });
    }
  };

  const handlePasswordBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorPass: errorsTxt.EPass });
    } else {
      if (e.currentTarget.value.length < 6) {
        setState({ ...state, errorPass: errorsTxt.EPassLength });
      } else {
        setState({ ...state, errorPass: "" });
      }
    }
  };

  const handleDobBlur = (_: React.SyntheticEvent<HTMLSelectElement>) => {
    const year = state.birthYear;
    const month = state.birthMonth;
    const day = state.birthDay;
    if (year > 0 && month > 0 && day > 0) {
      if (moment(new Date(year, month - 1, day)).isValid()) {
        const today = new Date();
        const birthDate = new Date(year, month - 1, day);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 15) {
          setState({ ...state, errorDob: errorsTxt.EDob, formDisabled: true });
        } else {
          setState({ ...state, errorDob: "", formDisabled: false });
        }
      } else {
        setState({ ...state, errorDob: errorsTxt.EDob, formDisabled: true });
      }
    }
  };

  const handleEmailBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorEmail: errorsTxt.EEmail });
    } else {
      setState({ ...state, errorEmail: "" });
    }
  };

  const handleRegionBlur = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    if (!e.currentTarget.value || e.currentTarget.value === "0") {
      setState({ ...state, errorRegion: errorsTxt.ERegion });
    } else {
      setState({ ...state, errorRegion: "" });
    }
  };

  const handleServerErrors = () => {
    const errors = getMode() === "register" ? registerError : updateError;
    const formattedErrors = [];
    if (errors) {
      if (
        errors.badRequest &&
        errors.badRequest.password &&
        errors.badRequest.password[0].code === "invalid"
      ) {
        formattedErrors.push(errorsTxt.EValidPass);
      }
      if (
        errors.badRequest &&
        errors.badRequest.email &&
        errors.badRequest.email[0].code === "duplicate"
      ) {
        formattedErrors.push(errorsTxt.EValidEmail);
      }
      if (
        errors.badRequest &&
        errors.badRequest.email &&
        errors.badRequest.email[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.EEmail);
      }
      if (
        errors.badRequest &&
        errors.badRequest.password &&
        errors.badRequest.password[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.EPass);
      }
      if (
        errors.badRequest &&
        errors.badRequest.last_name &&
        errors.badRequest.last_name[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.EFName);
      }
      if (
        errors.badRequest &&
        errors.badRequest.last_name &&
        errors.badRequest.last_name[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.ELName);
      }
      if (
        errors.badRequest &&
        errors.badRequest.date_of_birth &&
        errors.badRequest.date_of_birth[0].code === "invalid"
      ) {
        formattedErrors.push(errorsTxt.EValidDob);
      }
      if (
        errors.badRequest &&
        errors.badRequest.region &&
        errors.badRequest.region[0].code === "does_not_exist"
      ) {
        formattedErrors.push(errorsTxt.ERegion);
      }

      return formattedErrors;
    }
  };

  const isFormDisabled = () => {
    const requiredFieldsMissing = getRequiredFields().some((e) => !state[e]);
    return requiredFieldsMissing || state.formDisabled;
  };

  const mode = getMode();
  const title = {
    register: t("register.title.registration", "Registration"),
    confirm: t("register.title.confirm", "Confirm your details"),
    update: t("register.title.update", "Confirm your details"),
  };
  const buttonText = {
    register: t("register.label.register", "Register"),
    confirm: t("register.label.confirm", "Update"),
    update: t("register.label.update", "Update"),
  };
  const errors = handleServerErrors();
  const saudiResi = state.residenceData?.filter(
    (reg: IResidence) => reg.code === 185,
  );
  const intResidenceUn = state.residenceData?.filter(
    (reg: IResidence) => reg.code !== 185 && reg.code < 240,
  );
  const intResidence = sortBy(intResidenceUn, ["name"]);

  const intResidenceCitiesUn = state.residenceData?.filter(
    (reg: IResidence) => reg.code > 239,
  );
  const intResidenceCities = sortBy(intResidenceCitiesUn, ["name"]);

  const residenceCity =
    state.residenceData && state.residences && state.residences === 185
      ? true
      : false;
  return (
    <Wrapper>
      <Main>
        <Box mx={2}>
          <Title>{title[mode]}</Title>
          <p>
            {t(
              "register.required",
              "Fields marked with an asterisk (*) are required.",
            )}
          </p>
        </Box>

        {errors && <Alert type="error">{values(errors).map((r) => r)}</Alert>}
        <Copy>
          <form onSubmit={handleSubmit}>
            <Box>
              <InputField
                required
                id="ismRegisterEmail"
                type="email"
                hasErrors={!!state.errorEmail}
                label={t("register.label.email", "Email")}
                value={state.email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
              />
              {state.errorEmail && (
                <FieldFeedback>{state.errorEmail}</FieldFeedback>
              )}
            </Box>
            {mode === "register" && (
              <Box mt={2}>
                <InputField
                  id="ismRegisterPassword"
                  type="password"
                  hasErrors={!!state.errorPass}
                  label={t("register.label.password", "Password")}
                  value={state.password}
                  onChange={handlePasswordChange}
                  hint={t("register.hint.passsword", "Minimum 6 characters")}
                  onBlur={handlePasswordBlur}
                />
                {state.errorPass && (
                  <FieldFeedback>{state.errorPass}</FieldFeedback>
                )}
              </Box>
            )}
            <Box mt={2}>
              <InputField
                id="ismRegisterFirstName"
                hasErrors={!!state.errorFName}
                label={t("register.label.fName", "First Name")}
                value={state.firstName}
                onChange={handleFirstNameChange}
                onBlur={handleFirstnameBlur}
              />
              {state.errorFName && (
                <FieldFeedback>{state.errorFName}</FieldFeedback>
              )}
            </Box>
            <Box mt={2}>
              <InputField
                id="ismRegisterLastName"
                label={t("register.label.lName", "Last Name")}
                hasErrors={!!state.errorLName}
                value={state.lastName}
                onChange={handleLastNameChange}
                onBlur={handleLastnameBlur}
              />
              {state.errorLName && (
                <FieldFeedback>{state.errorLName}</FieldFeedback>
              )}
            </Box>
            <Box mt={2}>
              {state.errorRegion && (
                <FieldFeedback>{state.errorRegion}</FieldFeedback>
              )}
              <SelectField
                value={state.region}
                onChange={handleRegionChange}
                label={t("register.label.regionnn", "Nationality")}
                id="ismRegisterRegion"
                onBlur={handleRegionBlur}
              >
                <option value="0" aria-selected={!state.region}>
                  {t("register.label.select", "Select")}
                </option>
                <optgroup
                  label={t<string>("register.label.norway", "Saudi Arabia")}
                >
                  {values(getSaudiRegions()).map((r) => (
                    <option
                      value={r.id}
                      aria-selected={r.id === state.region}
                      key={r.id}
                    >
                      {r.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup
                  label={t<string>(
                    "register.label.international",
                    "International",
                  )}
                >
                  {values(getIntRegions()).map((r) => (
                    <option
                      value={r.id}
                      aria-selected={r.id === state.region}
                      key={r.id}
                    >
                      {r.name}
                    </option>
                  ))}
                </optgroup>
              </SelectField>
            </Box>
            <Box my={2}>
              <RadioGroup
                defaultOptionIndex={
                  state.gender === "M"
                    ? 0
                    : state.gender === "F"
                      ? 1
                      : undefined
                }
                legend={t("register.label.gender", "Gender")}
                name="gender"
                onChange={handleGenderChange}
                options={[
                  {
                    id: "ism-gender-male",
                    label: t("register.male", "Male"),
                    value: "M",
                  },
                  {
                    id: "ism-gender-female",
                    label: t("register.female", "Female"),
                    value: "F",
                  },
                ]}
              />
            </Box>
            <Box mt={2}>
              <DOBFieldset>
                <DOBLegend>
                  {t("register.label.dob", "Date of birth")}
                </DOBLegend>
                <span>
                  {t(
                    "register.hint.dob",
                    "The game is only available to individuals aged 15 and over",
                  )}
                </span>
                <DOBFieldsetInner>
                  <DOBCol>
                    <SelectField
                      labelVisibility={false}
                      id="ismRegisterBirthDay"
                      value={state.birthDay}
                      onChange={handleBirthDayChange}
                      label={t("register.label.day", "Day")}
                      onBlur={handleDobBlur}
                    >
                      <option
                        value="0"
                        aria-selected={!state.birthDay}
                        disabled
                        defaultValue="0"
                      >
                        {t("register.label.day", "Day")}
                      </option>
                      {range(1, 32).map((d) => (
                        <option
                          value={d}
                          aria-selected={d === state.birthDay}
                          key={d}
                        >
                          {d}
                        </option>
                      ))}
                    </SelectField>
                  </DOBCol>
                  <DOBCol>
                    <SelectField
                      labelVisibility={false}
                      id="ismRegisterBirthMonth"
                      value={state.birthMonth}
                      onChange={handleBirthMonthChange}
                      label={t("register.label.month", "Month")}
                      onBlur={handleDobBlur}
                    >
                      <option
                        value="0"
                        aria-selected={!state.birthMonth}
                        disabled
                        defaultValue="0"
                      >
                        {t("register.label.month", "Month")}
                      </option>
                      {Object.keys(months).map((m) => (
                        <option
                          value={m}
                          aria-selected={parseInt(m, 10) === state.birthMonth}
                          key={m}
                        >
                          {months[m]}
                        </option>
                      ))}
                    </SelectField>
                  </DOBCol>
                  <DOBCol>
                    <SelectField
                      labelVisibility={false}
                      value={state.birthYear}
                      onChange={handleBirthYearChange}
                      label={t("register.label.year", "Year")}
                      id="ismRegisterBirthYear"
                      onBlur={handleDobBlur}
                    >
                      <option
                        value="0"
                        aria-selected={!state.birthYear}
                        disabled
                        defaultValue="0"
                      >
                        {t("register.label.year", "Year")}
                      </option>
                      {range(new Date().getFullYear() - 15, 1900).map((y) => (
                        <option
                          value={y}
                          aria-selected={y === state.birthYear}
                          key={y}
                        >
                          {y}
                        </option>
                      ))}
                    </SelectField>
                  </DOBCol>
                </DOBFieldsetInner>
                {state.errorDob && (
                  <FieldFeedback>{state.errorDob}</FieldFeedback>
                )}
              </DOBFieldset>
            </Box>
            <Box mt={2}>
              <SelectField
                value={state.residences}
                onChange={handleResidenceChange}
                label={t("register.label.residence", "Residency")}
                id="ismRegisterResidence"
                required={false}
              >
                <option value="0" aria-selected={!state.residences}>
                  {t("register.label.select", "Select")}
                </option>
                <optgroup
                  label={t<string>("register.label.norway", "Saudi Arabia")}
                >
                  {values(saudiResi).map((r) => (
                    <option
                      value={r.id}
                      aria-selected={r.id === state.residences}
                      key={r.id}
                    >
                      {r.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup
                  label={t<string>(
                    "register.label.international",
                    "International",
                  )}
                >
                  {values(intResidence).map((r) => (
                    <option
                      value={r.id}
                      aria-selected={r.id === state.residences}
                      key={r.id}
                    >
                      {r.name}
                    </option>
                  ))}
                </optgroup>
              </SelectField>
            </Box>
            {residenceCity && (
              <Box mt={2}>
                <SelectField
                  onChange={handleResidenceCityChange}
                  label={t("register.label.residenceCity", "City of Residence")}
                  id="ismRegisterResidenceCity"
                  required={false}
                >
                  <option value="0" aria-selected={!state.residenceCity}>
                    {t("register.label.select", "Select")}
                  </option>
                  {values(intResidenceCities).map((r) => (
                    <option
                      value={r.id}
                      aria-selected={r.id === state.residenceCity}
                      selected={r.id === state.residenceCity}
                      key={r.id}
                    >
                      {r.name}
                    </option>
                  ))}
                </SelectField>
              </Box>
            )}
            <Box mt={2}>
              <p>
                {t(
                  "register.emailText",
                  "Receive information and newsletter from",
                )}
              </p>
              <CheckboxField
                checked={state.emailSPL}
                id="ismRegisteremailSPL"
                label={t("register.label.emailClient", "RSL")}
                onChange={handleemailSPLChange}
              />
            </Box>
            <Box mt={2} mb={8}>
              <Button type="submit" disabled={isFormDisabled()}>
                {buttonText[mode]}
              </Button>
            </Box>
          </form>
        </Copy>
        {mode === "update" && player && <PasswordChange />}
        {mode === "update" && player && <DeleteAccountBox />}
      </Main>
    </Wrapper>
  );
};

export { Register as RegisterTest };

export default withTranslation()(Register);
